// @ts-strict-ignore
import { getUserState } from '@carvana/core-auth';
import { hasMobileUserAgent } from '@carvana/stc-utilities';

import { recordMoMetricsEvent } from './uiMetrics';

declare namespace AndroidClientAnalytics {
  function recordAnalytics(eventName: string, metadata: {});
}

export type MobileAnalyticsPayload = {
  event: string;
  metadata: {};
};

declare global {
  interface WebKitMessageHandler {
    analytics?: {
      postMessage(_: MobileAnalyticsPayload): void;
    };
  }
}

export type MobileAnalyticsResult = {
  submitted: boolean;
  error?: string;
  exception?: Error;
  recorderType:
    | 'iOS'
    | 'Android'
    | 'UserAgentMismatch'
    | 'RecorderNotFound'
    | 'UnknownError'
    | 'None';
};

enum MobileAnalyticsEvents {
  PageVisited = 'stc_page_visited'
}

const NullError = 'Null Error';

const iosRecorder = (eventName: string, metadata = {}): MobileAnalyticsResult => {
  const recorder = window?.webkit?.messageHandlers?.analytics;
  if (recorder) {
    try {
      recorder.postMessage({
        event: eventName,
        metadata: {
          ...metadata,
          userId: getUserState().user.user_id
        }
      });
    } catch (err) {
      return {
        submitted: false,
        error: err?.toString() || NullError,
        exception: err instanceof Error ? err : null,
        recorderType: 'iOS'
      };
    }
    return {
      submitted: true,
      recorderType: 'iOS'
    };
  }
  return {
    submitted: false,
    recorderType: 'RecorderNotFound'
  };
};

const androidRecorder = (eventName: string, metadata = {}): MobileAnalyticsResult => {
  try {
    if (AndroidClientAnalytics?.recordAnalytics) {
      try {
        const metaString = JSON.stringify({
          ...(metadata || {}),
          userId: getUserState().user.user_id
        });
        AndroidClientAnalytics.recordAnalytics(eventName, metaString);
        return {
          submitted: true,
          recorderType: 'Android'
        };
      } catch (err) {
        return {
          submitted: false,
          error: err?.toString() || NullError,
          exception: err instanceof Error ? err : null,
          recorderType: 'Android'
        };
      }
    }
  } catch (err) {
    if (err instanceof ReferenceError) {
      return {
        submitted: false,
        recorderType: 'RecorderNotFound'
      };
    }
    return {
      submitted: false,
      error: err?.toString() || NullError,
      exception: err instanceof Error ? err : null,
      recorderType: 'Android'
    };
  }
  return {
    submitted: false,
    recorderType: 'RecorderNotFound'
  };
};

const internalRecordEvent = (eventName: string, metadata: object): MobileAnalyticsResult => {
  if (hasMobileUserAgent()) {
    try {
      const iosAttempt = iosRecorder(eventName, metadata);
      if (iosAttempt.submitted || iosAttempt.error) {
        return iosAttempt;
      }
      const androidAttempt = androidRecorder(eventName, metadata);
      if (androidAttempt.submitted || androidAttempt.error) {
        return androidAttempt;
      }
      return {
        submitted: false,
        error: 'Found mobile agent, but no recorder handled it',
        recorderType: 'UserAgentMismatch'
      };
    } catch (err) {
      return {
        submitted: false,
        error: err?.toString() || NullError,
        exception: err instanceof Error ? err : null,
        recorderType: 'UnknownError'
      };
    }
  }

  return {
    submitted: false,
    recorderType: 'None'
  };
};

const recordAnalyticsEventMobile = (eventName: string, metadata = {}): MobileAnalyticsResult => {
  const result = internalRecordEvent(eventName, metadata);
  const customAttributes = {
    eventName,
    metadata,
    result
  };
  recordMoMetricsEvent(
    {
      eventTime: new Date(),
      eventName: 'STC:MobileEventRecorded'
    },
    customAttributes
  );
  return result;
};

export default recordAnalyticsEventMobile;
export { MobileAnalyticsEvents, NullError };
